import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Col,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Badge,
  Alert,
  Spinner,
  Tab,
  Tabs,
  Card,
  ListGroup,
  InputGroup,
} from 'react-bootstrap';
import { api } from '../../providers/apiClient';
import { toast } from 'react-toastify';
import { formatTimestampToDate } from '../../utils';
import { formatTicketCode } from '../../utils/formatCodes';
import { useConfirmToast } from '../../hooks/useConfirmToast';
import { 
  MdSend, 
  MdEdit, 
  MdDelete, 
  MdComment, 
  MdHistory, 
  MdCheckCircle,
  MdCancel,
  MdAccessTime,
  MdPerson,
  MdSchedule
} from 'react-icons/md';
import styled from 'styled-components';

const CustomModalBody = styled(ModalBody)`
  max-height: 70vh;
  overflow-y: auto;
  padding: 1.5rem;
  
  .form-control {
    cursor: text !important;
    caret-color: #0d6efd !important;
    
    &:focus {
      cursor: text !important;
      caret-color: #0d6efd !important;
    }
  }
`;

const StatusBadge = styled(Badge)`
  font-size: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  
  &.status-aberto {
    background-color: #ffc107 !important;
    color: #000 !important;
  }
  
  &.status-em-andamento {
    background-color: #0d6efd !important;
    color: #fff !important;
  }
  
  &.status-concluido {
    background-color: #198754 !important;
    color: #fff !important;
  }
  
  &.status-cancelado {
    background-color: #dc3545 !important;
    color: #fff !important;
  }
  
  &.prioridade-baixa {
    background-color: #6c757d !important;
    color: #fff !important;
  }
  
  &.prioridade-media {
    background-color: #fd7e14 !important;
    color: #fff !important;
  }
  
  &.prioridade-alta {
    background-color: #dc3545 !important;
    color: #fff !important;
  }
`;

const CommentCard = styled(Card)`
  margin-bottom: 1rem;
  border-left: 4px solid #0d6efd;
  
  &.own-comment {
    border-left-color: #198754;
  }
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const CommentAuthor = styled.span`
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
`;

const CommentDate = styled.span`
  color: #6c757d;
  font-size: 0.8rem;
`;

const CommentActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding-left: 1rem;
  border-left: 2px solid #e9ecef;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 0.5rem;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #0d6efd;
  }
  
  &.comment-item::before {
    background-color: #198754;
  }
  
  &.status-change::before {
    background-color: #ffc107;
  }
`;

const TimelineContent = styled.div`
  flex: 1;
  margin-left: 1rem;
`;

const TimelineTitle = styled.div`
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.25rem;
`;

const TimelineDescription = styled.div`
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const TimelineTime = styled.div`
  color: #adb5bd;
  font-size: 0.8rem;
`;

const ModalTicketResponse = ({ 
  isModalOpened, 
  handleToggleModal, 
  ticket, 
  onTicketUpdate 
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const [isLoading, setIsLoading] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const { confirm } = useConfirmToast();
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [ticketData, setTicketData] = useState(ticket);
  const [statusUpdate, setStatusUpdate] = useState({
    status: ticket?.status || 'ABERTO',
    estagio: ticket?.estagio || ''
  });
  
  const commentTextareaRef = useRef(null);
  const editCommentTextareaRef = useRef(null);

  useEffect(() => {
    if (ticket) {
      setTicketData(ticket);
      setStatusUpdate({
        status: ticket.status || 'ABERTO',
        estagio: ticket.estagio || ''
      });
      fetchComments();
      fetchHistory();
    }
  }, [ticket]);

  useEffect(() => {
    if (commentTextareaRef.current) {
      commentTextareaRef.current.style.height = 'auto';
      commentTextareaRef.current.style.height = `${commentTextareaRef.current.scrollHeight}px`;
    }
  }, [newComment]);

  useEffect(() => {
    if (editCommentTextareaRef.current) {
      editCommentTextareaRef.current.style.height = 'auto';
      editCommentTextareaRef.current.style.height = `${editCommentTextareaRef.current.scrollHeight}px`;
    }
  }, [editCommentText]);

  const fetchComments = async () => {
    if (!ticket?.id) return;
    
    try {
      const response = await api.get(`/service_desk/${ticket.id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Erro ao buscar comentários:', error);
    }
  };

  const fetchHistory = async () => {
    if (!ticket?.id) return;
    
    try {
      const response = await api.get(`/service_desk/${ticket.id}/history`);
      setHistory(response.data);
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setIsLoading(true);
    try {
      const response = await api.post(`/service_desk/${ticket.id}/comments`, {
        message: newComment.trim()
      });
      
      setComments(prev => [...prev, response.data]);
      setNewComment('');
      onTicketUpdate?.();
      await fetchHistory(); // Recarregar histórico
    } catch (error) {
      console.error('Erro ao adicionar comentário:', error);
      toast.error('Erro ao adicionar comentário');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editCommentText.trim()) return;
    
    setIsLoading(true);
    try {
      const response = await api.put(`/service_desk/${ticket.id}/comments/${commentId}`, {
        message: editCommentText.trim()
      });
      
      setComments(prev => prev.map(comment => 
        comment.id === commentId ? response.data : comment
      ));
      setEditingComment(null);
      setEditCommentText('');
      onTicketUpdate?.();
      await fetchHistory(); // Recarregar histórico
    } catch (error) {
      console.error('Erro ao editar comentário:', error);
      toast.error('Erro ao editar comentário');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteComment = (commentId) => {
    confirm(
      'Tem certeza que deseja deletar este comentário?',
      () => confirmDeleteComment(commentId),
      null,
      {
        confirmText: "Sim, deletar!",
        cancelText: "Cancelar",
        variant: "danger"
      }
    );
  };

  const confirmDeleteComment = async (commentId) => {
    setIsLoading(true);
    try {
      await api.delete(`/service_desk/${ticket.id}/comments/${commentId}`);
      
      setComments(prev => prev.filter(comment => comment.id !== commentId));
      onTicketUpdate?.();
      toast.success('Comentário deletado com sucesso!');
      await fetchHistory(); // Recarregar histórico
    } catch (error) {
      console.error('Erro ao deletar comentário:', error);
      toast.error('Erro ao deletar comentário');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    setIsStatusLoading(true);
    try {
      const response = await api.patch(`/service_desk/${ticket.id}/status`, statusUpdate);
      
      setTicketData(response.data);
      onTicketUpdate?.();
      toast.success('Status atualizado com sucesso!');
      await fetchHistory(); // Recarregar histórico
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Erro ao atualizar status');
    } finally {
      setIsStatusLoading(false);
    }
  };

  const startEditComment = (comment) => {
    setEditingComment(comment.id);
    setEditCommentText(comment.message);
  };

  const cancelEditComment = () => {
    setEditingComment(null);
    setEditCommentText('');
  };

  const renderDetailsTab = () => (
    <div>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Secretaria:</Form.Label>
            <Form.Control
              type="text"
              value={ticketData?.branch?.name || 'N/A'}
              readOnly
              disabled
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Usuário:</Form.Label>
            <Form.Control
              type="text"
              value={ticketData?.usuario?.name || 'N/A'}
              readOnly
              disabled
            />
          </Form.Group>
        </Col>
      </Row>
      
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Unidade:</Form.Label>
            <Form.Control
              type="text"
              value={ticketData?.unit?.name || 'N/A'}
              readOnly
              disabled
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Departamento:</Form.Label>
            <Form.Control
              type="text"
              value={ticketData?.department?.name || 'N/A'}
              readOnly
              disabled
            />
          </Form.Group>
        </Col>
      </Row>
      
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Prioridade:</Form.Label>
            <Form.Control
              type="text"
              value={ticketData?.prioridade || 'N/A'}
              readOnly
              disabled
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Categoria:</Form.Label>
            <Form.Control
              type="text"
              value={ticketData?.categoria || 'N/A'}
              readOnly
              disabled
            />
          </Form.Group>
        </Col>
      </Row>
      
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Status Atual:</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <StatusBadge className={`status-${(ticketData?.status || 'ABERTO').toLowerCase().replace('_', '-')}`}>
                {ticketData?.status || 'ABERTO'}
              </StatusBadge>
              {ticketData?.prioridade && (
                <StatusBadge className={`prioridade-${ticketData.prioridade.toLowerCase()}`}>
                  {ticketData.prioridade}
                </StatusBadge>
              )}
            </div>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Telefone:</Form.Label>
            <Form.Control
              type="text"
              value={ticketData?.telefone || 'N/A'}
              readOnly
              disabled
            />
          </Form.Group>
        </Col>
      </Row>
      
      <Form.Group className="mb-3">
        <Form.Label>Assunto</Form.Label>
        <Form.Control
          as="textarea"
          rows="2"
          value={ticketData?.resumo || 'N/A'}
          readOnly
          disabled
          style={{ 
            overflow: 'auto', 
            resize: 'none',
            maxHeight: '80px',
            fontFamily: 'Nunito, sans-serif'
          }}
        />
      </Form.Group>
      
      <Form.Group className="mb-3">
        <Form.Label>Descrição</Form.Label>
        <Form.Control
          as="textarea"
          rows="6"
          value={ticketData?.descricao}
          readOnly
          disabled
          style={{ 
            overflow: 'auto', 
            resize: 'none',
            maxHeight: '200px',
            fontFamily: 'Nunito, sans-serif'
          }}
        />
      </Form.Group>
      
      <Form.Group className="mb-3">
        <Form.Label>Observação</Form.Label>
        <Form.Control
          as="textarea"
          rows="4"
          value={ticketData?.observacao || 'Nenhuma observação'}
          readOnly
          disabled
          style={{ 
            overflow: 'auto', 
            resize: 'none',
            maxHeight: '150px',
            fontFamily: 'Nunito, sans-serif'
          }}
        />
      </Form.Group>
    </div>
  );

  const renderResponseTab = () => (
    <div>
      <Alert variant="info" className="mb-3">
        <MdComment className="me-2" />
        Use esta seção para responder ao chamado, atualizar seu status e gerenciar comentários.
      </Alert>
      
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Status do Chamado:</Form.Label>
            <Form.Select
              value={statusUpdate.status}
              onChange={(e) => setStatusUpdate(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="ABERTO">Aberto</option>
              <option value="EM_ANDAMENTO">Em Andamento</option>
              <option value="CONCLUIDO">Concluído</option>
              <option value="CANCELADO">Cancelado</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Estágio:</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ex: Análise, Desenvolvimento, Teste, etc."
              value={statusUpdate.estagio}
              onChange={(e) => setStatusUpdate(prev => ({ ...prev, estagio: e.target.value }))}
              style={{ 
                cursor: 'text',
                caretColor: '#0d6efd'
              }}
            />
          </Form.Group>
        </Col>
      </Row>
      
      <div className="d-flex justify-content-end mb-4">
        <Button 
          variant="primary" 
          onClick={handleUpdateStatus}
          disabled={isStatusLoading}
        >
          {isStatusLoading ? <Spinner size="sm" className="me-2" /> : <MdCheckCircle className="me-2" />}
          Atualizar Status
        </Button>
      </div>
      
      <hr className="my-4" />
      
      <h6 className="mb-3">Comentários</h6>
      
      <div className="mb-3">
        <Form.Group>
          <Form.Label>Adicionar Comentário:</Form.Label>
          <Form.Control
            ref={commentTextareaRef}
            as="textarea"
            rows="3"
            placeholder="Digite seu comentário..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            style={{ 
              resize: 'none',
              cursor: 'text',
              caretColor: '#0d6efd'
            }}
          />
        </Form.Group>
        <div className="d-flex justify-content-end mt-2">
          <Button 
            variant="primary" 
            size="sm"
            onClick={handleAddComment}
            disabled={isLoading || !newComment.trim()}
          >
            {isLoading ? <Spinner size="sm" className="me-1" /> : <MdSend className="me-1" />}
            Enviar
          </Button>
        </div>
      </div>
      
      <div className="comments-list">
        {comments.length === 0 ? (
          <Alert variant="light" className="text-center">
            Nenhum comentário ainda. Seja o primeiro a comentar!
          </Alert>
        ) : (
          comments.map((comment) => (
            <CommentCard key={comment.id} className={comment.author?.id === ticketData?.usuario?.id ? 'own-comment' : ''}>
              <Card.Body>
                <CommentHeader>
                  <div>
                    <CommentAuthor>
                      <MdPerson className="me-1" />
                      {comment.author?.name || 'Usuário'}
                    </CommentAuthor>
                    <CommentDate className="ms-2">
                      <MdSchedule className="me-1" />
                      {formatTimestampToDate(comment.createdAt)}
                    </CommentDate>
                  </div>
                  <CommentActions>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => startEditComment(comment)}
                    >
                      <MdEdit />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteComment(comment.id)}
                    >
                      <MdDelete />
                    </Button>
                  </CommentActions>
                </CommentHeader>
                
                {editingComment === comment.id ? (
                  <div>
                    <Form.Control
                      ref={editCommentTextareaRef}
                      as="textarea"
                      rows="3"
                      value={editCommentText}
                      onChange={(e) => setEditCommentText(e.target.value)}
                      style={{ 
                        resize: 'none',
                        cursor: 'text',
                        caretColor: '#0d6efd'
                      }}
                    />
                    <div className="d-flex justify-content-end gap-2 mt-2">
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleEditComment(comment.id)}
                        disabled={isLoading}
                      >
                        Salvar
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={cancelEditComment}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {comment.message}
                  </div>
                )}
              </Card.Body>
            </CommentCard>
          ))
        )}
      </div>
    </div>
  );


  const renderHistoryTab = () => (
    <div>
      {history ? (
        <div>
          <h6 className="mb-3">Histórico do Chamado</h6>
          <div className="timeline">
            {history.timeline?.map((item, index) => (
              <TimelineItem 
                key={index}
                className={`${item.type === 'comment_added' ? 'comment-item' : ''} ${item.type === 'status_changed' ? 'status-change' : ''}`}
              >
                <TimelineContent>
                  <TimelineTitle>
                    {item.type === 'created' && 'Chamado criado'}
                    {item.type === 'comment_added' && 'Comentário adicionado'}
                    {item.type === 'comment_edited' && 'Comentário editado'}
                    {item.type === 'comment_deleted' && 'Comentário deletado'}
                    {item.type === 'status_changed' && 'Status alterado'}
                    {item.type === 'updated' && 'Ticket atualizado'}
                  </TimelineTitle>
                  <TimelineDescription>
                    {item.description}
                  </TimelineDescription>
                  {item.oldValue && item.newValue && (
                    <div className="mt-2 p-2 bg-light rounded">
                      <div className="text-muted small">De:</div>
                      <div className="text-decoration-line-through text-muted">{item.oldValue}</div>
                      <div className="text-muted small mt-1">Para:</div>
                      <div className="text-success">{item.newValue}</div>
                    </div>
                  )}
                  {item.newValue && !item.oldValue && (
                    <div className="mt-2 p-2 bg-light rounded" style={{ whiteSpace: 'pre-wrap' }}>
                      {item.newValue}
                    </div>
                  )}
                  <TimelineTime>
                    Por {item.user?.name || 'Usuário'} em {formatTimestampToDate(item.timestamp)}
                  </TimelineTime>
                </TimelineContent>
              </TimelineItem>
            ))}
          </div>
        </div>
      ) : (
        <Alert variant="light" className="text-center">
          <Spinner size="sm" className="me-2" />
          Carregando histórico...
        </Alert>
      )}
    </div>
  );

  if (!ticket) return null;

  return (
    <Modal
      id="modal-ticket-response"
      show={isModalOpened}
      centered
      onHide={handleToggleModal}
      size="xl"
    >
      <ModalHeader id="modal-ticket-response-header" closeButton>
        <div className="d-flex align-items-center">
          <MdComment className="me-2" />
          Chamado #{formatTicketCode(ticket.id)} - {ticket.resumo}
        </div>
      </ModalHeader>
      
      <CustomModalBody id="modal-ticket-response-body">
        <Tabs
          id="ticket-tabs"
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          <Tab eventKey="details" title="Detalhes">
            {renderDetailsTab()}
          </Tab>
          <Tab eventKey="response" title={`Responder & Comentários (${comments.length})`}>
            {renderResponseTab()}
          </Tab>
          <Tab eventKey="history" title="Histórico">
            {renderHistoryTab()}
          </Tab>
        </Tabs>
      </CustomModalBody>
      
      <ModalFooter>
        <Button variant="light" onClick={handleToggleModal}>
          Fechar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ModalTicketResponse;
